<template>
  <div>
    <breadcrumbs :home="product.name + ': Move to site'"/>

      <v-card flat>
        <v-form v-model="valid" ref="form" :disabled="!edit">
          <v-container class="pt-6 px-6 purtrak-scrollable" fluid>
              <v-row dense v-if="sites.length === 1 && !transactionId">
                <v-col class="warning--text">
                  There must be multiple sites in order to transfer
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6" sm="4" lg="3">
                  <date-picker v-model="transaction.date" @change="refresh"/>
                </v-col>
              </v-row>

              <v-row dense class="mb-4">
                <v-col>
                  <in-bond v-model="transaction.bonded" @change="changeBond"/>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="12" sm="6" lg="4">
                  <v-autocomplete outlined dense label="From site" v-model="transaction.fromSiteId" :items="sites" item-value="id" item-text="name" :rules="[fieldRules.required]"/>
                </v-col>
                <v-col cols="12" sm="6" lg="4">
                  <v-autocomplete outlined dense label="To site" v-model="transaction.toSiteId" :items="sites" item-value="id" item-text="name" :rules="[fieldRules.required]"/>
                </v-col>
              </v-row>

              <v-row dense>
                <v-col cols="6" sm="3" lg="2">
                  <decimal-number-input label="Cases" v-model="transaction.cases" :precision="0" integer prefix="" suffix=""/>
                </v-col>
                <v-col cols="6" sm="3" lg="2">
                  <decimal-number-input label="Bottles" v-model="transaction.bottles" :precision="0" integer prefix="" suffix=""/>
                </v-col>
              </v-row>
              <v-row dense v-if="company.settings.lotTrackingForProducts && (transaction.bonded || company.settings.lotTrackingForDutyPaid)">
                <v-col cols="6" sm="3" lg="2">
                  <v-combobox v-model="transaction.lot" outlined dense label="Lot" :items="currentLots" :rules="[fieldRules.required,fieldRules.maxLength(20)]"/>
                </v-col>
              </v-row>

              <v-row dense >
                <v-col>
                  <v-text-field label="Comments" v-model="transaction.comments" outlined dense :rules="[fieldRules.maxLength(255)]" counter="255"/>
                </v-col>
              </v-row>
            </v-container>
        </v-form>
        <v-divider/>
        <v-card-actions class="px-6">
          <template v-if="transactionId && company.permissions.includes('inventory.write')">
            <v-switch v-if="transaction.date > company.excisePeriodEnd || edit" :disabled="dirty"
                      v-model="edit" label="Edit" hide-details class="mt-0"/>
            <div v-else class="grey--text">Cannot edit since excise filed to period end {{company.excisePeriodEnd | formatDate}}</div>
          </template>
          <v-spacer/>
          <v-btn :plain="edit" color="primary" @click="cancel">{{edit?'Cancel':'Done'}}</v-btn>
          <v-btn v-if="edit" color="primary" :loading="saving" @click="save" :disabled="sites.length === 1 && !transactionId">Save</v-btn>
        </v-card-actions>
      </v-card>

    <confirm-leave v-model="next"/>
  </div>

</template>

<script>
import {format} from 'date-fns'
import confirmLeave from '@/mixins/confirmLeave'
import InBond from '@/components/InBond'

export default {
  name: 'InventoryProductsTransferSite',
  mixins: [confirmLeave],
  components: {InBond},
	props: {
    productId: {type:String,required:false},
    transactionId: {type:String, required:false}
	},
	data() {
		return {
      edit: false,
      product: {sites:[]},
      valid:true,
      saving:false,
			transaction: {productId:this.productId, date: format(new Date(), 'yyyy-MM-dd HH:mm:ss'), cases:0, bottles: 0,lot:'', bonded:null, fromSiteId: null,toSiteId:null, comments:''},
      dirty: false
		}
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(!this.transactionId, this.transaction.bonded ? {B262:true} : undefined);
    },
    currentLots() {
      return this.product.sites.filter(site => site.siteId === this.transaction.fromSiteId &&
                                              (this.transaction.bonded && site.inBondBottles || !this.transaction.bonded && site.dutyPaidBottles))
                                .map(site => site.lot);
    }
  },
  watch: {
    transaction: {
      handler() {this.dirty = true;},
      immediate: true,
      deep: true
    }
  },
	async created() {
    if (this.transactionId) {
      const results = await this.$apiCall(`query InventoryProductsTransferSite($id:ID!) {
                                                productTransferSite(id:$id) {
                                                  id,productId,date,fromSiteId,toSiteId,cases,lot,bottles,bonded,comments
                                                }
                                              }`,
                                          {id:this.transactionId});
      if (results.error) {
        this.$nextTick(() => this.dirty = false);
        this.$router.back();
        return;
      }
      this.transaction = results.data.productTransferSite;
      this.quantity = this.transaction.bottles;
      await this.refresh();
    }
    else {
      this.transaction.fromSiteId = this.sites[0].id;
      this.transaction.toSiteId = this.sites[1].id;
      this.edit = true;
    }

    await this.refresh();
    this.$nextTick(() => this.dirty = false);
  },

	methods: {
    changeBond() {
      if (!this.sites.some(site => site.id === this.transaction.fromSiteId))
        this.transaction.fromSiteId = this.sites[0].id;
      if (!this.sites.some(site => site.id === this.transaction.toSiteId))
        this.transaction.toSiteId = this.sites[0].id;
      this.$nextTick(() => this.$refs.form.validate());
    },
    async refresh() {
      const results = await this.$apiCall(`query InventoryProductsTransferSite($id:ID!,$asof:DateTime!) {
                                                product(id:$id) {
                                                  id,name,caseSize,
                                                  sites(asof:$asof) {siteId,lot,inBondBottles,dutyPaidBottles}
                                                }
                                              }`,
                                          {id:this.transaction ? this.transaction.productId : this.productId,
                                           asof:this.transaction.date});
      if (!results.error) {
        this.product = results.data.product;
        this.$refs.form.validate();
      }
    },
    cancel() {
      this.$router.back();
    },
		async save() {
      this.$refs.form.validate();
      if (!this.valid) {
        this.$store.commit('setError', {type:'warning',message:`One or more fields is missing or invalid`});
        return;
      }
      this.saving = true;
      const results = await this.$apiCall(`mutation InventoryProductsTransferSite($input: ProductTransferSiteInput!) {
                                            productTransferSiteCreate(input:$input) {id}
                                          }`,
                                          {input:this.transaction});
      this.saving = false;
      if (!results.error) {
        this.dirty = false;
        this.$router.back();
        this.$store.commit('setError', {type:'success',message:`${this.transactionId?'Updated':'Created'} move to site transaction for ${this.product.name}`});
      }
		}
	}
}
</script>